import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api, keys, useMe } from '../../api.ts';
import { reportFiltersSchema, type ReportFilters } from '../../../shared/schemas.ts';
import type { ReportResponse, TableReport } from '../../../shared/types.ts';
import { ReportTable } from '../../components/ReportTable.tsx';
import { BandPicker, CountryPicker, ErrorBox, Field, Help, ModePicker, PageHeader, Spinner, useDocumentTitle, useToast } from '../../components/ui.tsx';

const EMPTY: ReportFilters = {
  bands: [],
  modes: [],
  countries: [],
  genre: null,
  callSign: '',
  licenceMinYears: null,
  licenceMaxYears: null,
  groupBy: [],
};

const saveForm = z.object({ name: z.string().trim().min(1, 'Anna taulukolle nimi').max(100), heading: z.string().max(500) });

export function ReportSearchPage() {
  useDocumentTitle('Taulukkohaku');
  const { data: me } = useMe();
  const [params] = useSearchParams();
  const fromId = Number(params.get('from')) || null;
  const [filters, setFilters] = useState<ReportFilters>(EMPTY);
  const [preview, setPreview] = useState<{ data: TableReport; filters: ReportFilters } | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // Olemassa olevan taulukon muokkaus: haetaan sen ehdot lomakkeelle
  const existing = useQuery({
    queryKey: keys.report(fromId ?? 0),
    queryFn: () => api.get<ReportResponse>(`/reports/${fromId}`),
    enabled: !!fromId,
  });
  const form = useForm<z.input<typeof saveForm>>({ resolver: zodResolver(saveForm), defaultValues: { name: '', heading: '' } });
  useEffect(() => {
    if (existing.data?.filters) {
      setFilters(existing.data.filters);
      form.reset({ name: existing.data.name, heading: existing.data.heading });
    }
  }, [existing.data, form]);

  const run = useMutation({
    mutationFn: (f: ReportFilters) => {
      const parsed = reportFiltersSchema.parse(f);
      return api.post<TableReport>('/reports/preview', { filters: parsed }).then((data) => ({ data, filters: parsed }));
    },
    onSuccess: setPreview,
  });
  const save = useMutation({
    mutationFn: (v: z.output<typeof saveForm>) => {
      const body = { ...v, filters: reportFiltersSchema.parse(filters) };
      return fromId ? api.put(`/reports/${fromId}`, body).then(() => ({ id: fromId })) : api.post<{ id: number }>('/reports', body);
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: keys.reports });
      void qc.invalidateQueries({ queryKey: ['report'] });
      toast.ok('Taulukko tallennettu');
      navigate(`/reports/${r.id}`);
    },
    onError: toast.error,
  });

  const licenceError = filters.licenceMinYears !== null && filters.licenceMaxYears !== null && filters.licenceMaxYears <= filters.licenceMinYears;
  const set = <K extends keyof ReportFilters>(k: K, v: ReportFilters[K]) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleGroup = (g: ReportFilters['groupBy'][number]) =>
    set('groupBy', filters.groupBy.includes(g) ? filters.groupBy.filter((x) => x !== g) : [...filters.groupBy, g]);
  const years = Array.from({ length: 100 }, (_, i) => i);

  return (
    <div className="page">
      <PageHeader
        title={fromId ? `Muokkaa: ${existing.data?.name ?? ''}` : 'Taulukkohaku'}
        sub="Rakenna oma taulukko: montako eri maata kukin asema on workkinut valituilla ehdoilla."
        actions={<Help name="HakuLomake" />}
      />
      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!licenceError) run.mutate(filters);
        }}
      >
        <div className="grid grid-2">
          <div className="stack">
            <div>
              <div className="label" style={{ marginBottom: 6 }}>
                Bandit
              </div>
              <BandPicker value={filters.bands} onChange={(v) => set('bands', v)} />
            </div>
            <div>
              <div className="label" style={{ marginBottom: 6 }}>
                Moodit
              </div>
              <ModePicker value={filters.modes} onChange={(v) => set('modes', v)} />
            </div>
            <div className="form-grid">
              <fieldset>
                <legend>Sukupuoli</legend>
                <div className="radio-group">
                  {([null, 'OM', 'YL'] as const).map((g) => (
                    <label key={g ?? 'all'} className="check">
                      <input type="radio" checked={filters.genre === g} onChange={() => set('genre', g)} /> {g ?? 'Kaikki'}
                    </label>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend>Ryhmittele sarakkeiksi</legend>
                <div className="radio-group">
                  <label className="check">
                    <input type="checkbox" checked={filters.groupBy.includes('band')} onChange={() => toggleGroup('band')} /> band
                  </label>
                  <label className="check">
                    <input type="checkbox" checked={filters.groupBy.includes('mode')} onChange={() => toggleGroup('mode')} /> moodi
                  </label>
                  <label className="check">
                    <input type="checkbox" checked={filters.groupBy.includes('parent_mode')} onChange={() => toggleGroup('parent_mode')} /> päämoodi
                  </label>
                </div>
              </fieldset>
            </div>
            <fieldset>
              <legend>Lisenssin ikä</legend>
              <div className="row">
                <span>vähintään</span>
                <select
                  style={{ width: 110 }}
                  value={filters.licenceMinYears ?? ''}
                  onChange={(e) => set('licenceMinYears', e.target.value === '' ? null : Number(e.target.value))}
                >
                  <option value="">–</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y} v
                    </option>
                  ))}
                </select>
                <span>ja alle</span>
                <select
                  style={{ width: 110 }}
                  value={filters.licenceMaxYears ?? ''}
                  aria-invalid={licenceError}
                  onChange={(e) => set('licenceMaxYears', e.target.value === '' ? null : Number(e.target.value))}
                >
                  <option value="">–</option>
                  {years.slice(1).map((y) => (
                    <option key={y} value={y}>
                      {y} v
                    </option>
                  ))}
                </select>
              </div>
              {licenceError && <div className="error small" style={{ color: 'var(--danger)' }}>Ylärajan on oltava suurempi kuin alaraja</div>}
            </fieldset>
            <div className="field" style={{ maxWidth: 260 }}>
              <label htmlFor="call">Workittu kutsu</label>
              <input id="call" className="input mono" value={filters.callSign} onChange={(e) => set('callSign', e.target.value)} />
            </div>
          </div>
          <div>
            <div className="label" style={{ marginBottom: 6 }}>
              Maat
            </div>
            <CountryPicker value={filters.countries} onChange={(v) => set('countries', v)} />
          </div>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={run.isPending || licenceError}>
            {run.isPending ? <Spinner /> : 'Hae esikatseluun'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setFilters(EMPTY);
              setPreview(null);
            }}
          >
            Tyhjennä
          </button>
        </div>
      </form>

      <ErrorBox error={run.error} />
      {preview && (
        <div className="stack" style={{ marginTop: 20 }}>
          {me?.isAdmin && (
            <form className="card" onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
              <div className="card-title">
                <h2>{fromId ? 'Tallenna muutokset taulukkoon' : 'Tallenna taulukoksi'}</h2>
              </div>
              <div className="form-grid">
                <Field label="Nimi" error={form.formState.errors.name} htmlFor="rname">
                  <input id="rname" className="input" {...form.register('name')} />
                </Field>
                <Field label="Kuvaus" error={form.formState.errors.heading} htmlFor="rheading">
                  <input id="rheading" className="input" {...form.register('heading')} />
                </Field>
              </div>
              <div className="form-actions" style={{ marginTop: 12 }}>
                <button className="btn btn-primary" type="submit" disabled={save.isPending}>
                  Tallenna
                </button>
              </div>
            </form>
          )}
          <h2>Esikatselu · {preview.data.rows.length} asemaa</h2>
          <ReportTable data={preview.data} filters={preview.filters} linkCells meId={me?.id} />
        </div>
      )}
    </div>
  );
}
